/**
 * The rows of a Query Editor tab's last results that carry one table column —
 * a filter chip's sample of that column (the Declare-as-JSON preview) without
 * reading the table again. A result column counts only when the engine
 * reported its provenance (`sourceTable` / `sourceColumn`, plus
 * `sourceSchema` when both sides have one): a same-named column of another
 * table, or of an expression, is never taken for it. Engines that report no
 * provenance (SQLite) never match — the chip then reads a small sample itself.
 */

import type { QueryResultSet } from '../../../../types/queryExecution';

export type ResultColumnTarget = {
  readonly schema?: string;
  readonly table: string;
  readonly column: string;
};

const same = (a: string, b: string): boolean => a.toLowerCase() === b.toLowerCase();

/** Rows keyed by `target.column`, from the first result set that carries it; undefined when none does. */
export function resultRowsForColumn(
  resultSets: readonly QueryResultSet[] | undefined,
  target: ResultColumnTarget,
): Record<string, unknown>[] | undefined {
  for (const rs of resultSets ?? []) {
    const col = rs.columns.find(
      (c) =>
        !!c.sourceTable &&
        !!c.sourceColumn &&
        same(c.sourceTable, target.table) &&
        same(c.sourceColumn, target.column) &&
        (!c.sourceSchema || !target.schema || same(c.sourceSchema, target.schema)),
    );
    if (!col || rs.rows.length === 0) continue;
    const key = col.id ?? col.name;
    return rs.rows.map((row) => ({ [target.column]: row[key] }));
  }
  return undefined;
}
